import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {
	ArrowLeft,
	BookOpen,
	ChartLine,
	ChatCircle,
	CursorClick,
	EnvelopeOpen,
	FileText,
	Fire,
	Globe,
	GraduationCap,
	House,
	LinkedinLogo,
	MagnifyingGlass,
	Microphone,
	Play,
	SpotifyLogo,
	TwitterLogo,
	Warning,
} from '@phosphor-icons/react';
import {SigninForm} from '../molecules/SigninForm';
import {NEUGENCE_API_BASE, bearerHeaders, fmtAgo, PERIOD_OPTIONS, useSortableTable, SortHeader} from './_lib';

const STATUS_BADGE = {
	hot_replied: {label: '🔥 REPLIED',    cls: 'bg-rose-100 text-rose-800 border border-rose-200'},
	hot:         {label: '🔥 HOT',        cls: 'bg-amber-100 text-amber-800 border border-amber-200'},
	warm_click:  {label: 'click',         cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200'},
	auto_reply:  {label: '🤖 auto-reply', cls: 'bg-slate-100 text-slate-500 border border-slate-200 line-through opacity-70'},
	warm_opens:  {label: 'opens',         cls: 'bg-sky-100 text-sky-800 border border-sky-200'},
	cool:        {label: '—',             cls: 'bg-slate-100 text-slate-600 border border-slate-200'},
};

const PERSONA_BADGE_CLS = {
	pmc_author:             'bg-violet-50 text-violet-800 border-violet-200',
	pubmed_researcher:      'bg-violet-50 text-violet-800 border-violet-200',
	production_association: 'bg-orange-50 text-orange-800 border-orange-200',
	podcast_host_uk:        'bg-blue-50 text-blue-800 border-blue-200',
	podcast_host_nl:        'bg-blue-50 text-blue-800 border-blue-200',
	podcast_host_de:        'bg-blue-50 text-blue-800 border-blue-200',
	podcast_host_in:        'bg-blue-50 text-blue-800 border-blue-200',
	common_crawl_creator:   'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200',
	transcript_audience:    'bg-slate-50 text-slate-700 border-slate-200',
};

const ICON_MAP = {
	MagnifyingGlass: MagnifyingGlass,
	Globe: Globe,
	House: House,
	LinkedinLogo: LinkedinLogo,
	TwitterLogo: TwitterLogo,
	GraduationCap: GraduationCap,
	BookOpen: BookOpen,
	Microphone: Microphone,
	SpotifyLogo: SpotifyLogo,
};

function SocialIcons({email, name}) {
	// Lazy-fetch the social link bundle once per recipient — the dashboard
	// renders dozens of rows so we hit the API only when the row mounts and
	// then memoize. Server-side it's pure URL construction (no scraping).
	const [links, setLinks] = useState(null);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		let alive = true;
		(async () => {
			try {
				const headers = await bearerHeaders();
				const res = await axios.get(
					`${NEUGENCE_API_BASE}/api/v1/recipients/${encodeURIComponent(email)}/social`,
					{headers},
				);
				if (alive) setLinks((res.data && res.data.links) || []);
			} catch (_e) {
				if (alive) setLinks([]);
			}
		})();
		return () => { alive = false; };
	}, [email]);

	if (!links) return <span className="text-xs text-slate-400">…</span>;
	if (links.length === 0) return <span className="text-xs text-slate-400">—</span>;
	const visible = open ? links : links.slice(0, 4);
	return (
		<div className="flex items-center gap-1 flex-wrap">
			{visible.map((l) => {
				const Icon = ICON_MAP[l.icon] || Globe;
				return (
					<a
						key={l.key}
						href={l.url}
						target="_blank"
						rel="noreferrer"
						title={`${l.label} — ${name || email}`}
						className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700"
					>
						<Icon size={13} />
					</a>
				);
			})}
			{links.length > 4 && (
				<button
					onClick={() => setOpen((v) => !v)}
					className="text-[10px] text-slate-500 hover:text-slate-900 px-1"
				>
					{open ? '−' : `+${links.length - 4}`}
				</button>
			)}
		</div>
	);
}

export default function HotRecipients() {
	const navigate = useNavigate();
	const [period, setPeriod] = useState('24h');
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [filter, setFilter] = useState('hot'); // hot | all
	const [country, setCountry] = useState('');
	const [persona, setPersona] = useState('');
	const [category, setCategory] = useState('');
	const [minScore, setMinScore] = useState(0);

	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const headers = await bearerHeaders();
			const res = await axios.get(`${NEUGENCE_API_BASE}/api/v1/recipients/hot?period=${period}`, {headers});
			setData(res.data || {});
		} catch (e) {
			setError(e.message || 'load failed');
		} finally {
			setLoading(false);
		}
	}, [period]);

	useEffect(() => { if (signedIn) load(); }, [load, signedIn]);

	const items = useMemo(() => (data && data.items) || [], [data]);

	const facetCountries = useMemo(() => {
		const set = new Set();
		items.forEach((r) => { if (r.country) set.add(r.country); });
		return Array.from(set).sort();
	}, [items]);
	const facetPersonas = useMemo(() => {
		const set = new Set();
		items.forEach((r) => { if (r.persona) set.add(r.persona); });
		return Array.from(set).sort();
	}, [items]);
	const facetCategories = useMemo(() => {
		const set = new Set();
		items.forEach((r) => { if (r.category) set.add(r.category); });
		return Array.from(set).sort();
	}, [items]);

	const counts = items.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

	const visible = items.filter((r) => {
		if (filter === 'hot' && !(r.status === 'hot_replied' || r.status === 'hot' || r.status === 'warm_click')) return false;
		if (country && r.country !== country) return false;
		if (persona && r.persona !== persona) return false;
		if (category && r.category !== category) return false;
		if (minScore > 0 && (r.score || 0) < minScore) return false;
		return true;
	});
	const sort = useSortableTable(visible, 'score', 'desc');

	if (!signedIn) return <div style={{marginTop: 300}}><SigninForm/></div>;

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-7xl mx-auto px-6 py-10">
				<button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/> Dashboard
				</button>

				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-3">
							<Fire size={28} weight="fill" color="#ef4444"/> Hot recipients
						</h1>
						<p className="text-sm text-slate-500 mt-1">Who's actually engaging — sorted by reply &gt; click &gt; recent open. Apple-MPP-distorted opens are deprioritised.</p>
					</div>
					<div role="tablist" className="inline-flex rounded-full bg-slate-100 p-1 self-start md:self-auto">
						{PERIOD_OPTIONS.map(p => (
							<button key={p.key} onClick={() => setPeriod(p.key)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${period===p.key?'bg-white text-slate-900 shadow-sm':'text-slate-600 hover:text-slate-900'}`}>
								{p.label}
							</button>
						))}
					</div>
				</div>

				{/* status pills */}
				<div className="flex flex-wrap gap-2 mb-4 text-xs">
					<span className="rounded-full bg-rose-50 border border-rose-200 px-3 py-1 text-rose-800">replied (human): <strong>{counts.hot_replied || 0}</strong></span>
					<span className="rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-amber-800">hot: <strong>{counts.hot || 0}</strong></span>
					<span className="rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-emerald-800">click: <strong>{counts.warm_click || 0}</strong></span>
					<span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-slate-500">🤖 auto-reply: <strong>{counts.auto_reply || 0}</strong></span>
					<span className="rounded-full bg-sky-50 border border-sky-200 px-3 py-1 text-sky-800">opens: <strong>{counts.warm_opens || 0}</strong></span>
					<span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-slate-600">cool: <strong>{counts.cool || 0}</strong></span>
					<button onClick={() => setFilter(filter === 'hot' ? 'all' : 'hot')} className="ml-auto rounded-full bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1">
						{filter === 'hot' ? 'show all' : 'hide cool/opens/auto'}
					</button>
				</div>

				{/* filters bar */}
				<div className="flex flex-wrap items-end gap-3 mb-6 p-3 rounded-xl border border-slate-200 bg-slate-50/40 text-xs">
					<label className="flex flex-col gap-1">
						<span className="text-slate-500">Country</span>
						<select value={country} onChange={(e) => setCountry(e.target.value)} className="rounded-md border border-slate-200 bg-white px-2 py-1 min-w-[100px]">
							<option value="">All</option>
							{facetCountries.map((c) => <option key={c} value={c}>{c}</option>)}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-slate-500">Persona</span>
						<select value={persona} onChange={(e) => setPersona(e.target.value)} className="rounded-md border border-slate-200 bg-white px-2 py-1 min-w-[160px]">
							<option value="">All</option>
							{facetPersonas.map((p) => <option key={p} value={p}>{p}</option>)}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-slate-500">Category</span>
						<select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-md border border-slate-200 bg-white px-2 py-1 min-w-[120px]">
							<option value="">All</option>
							{facetCategories.map((c) => <option key={c} value={c}>{c}</option>)}
						</select>
					</label>
					<label className="flex flex-col gap-1 flex-1 min-w-[180px]">
						<span className="text-slate-500">Min score: <strong>{minScore}</strong></span>
						<input type="range" min="0" max="50" step="1" value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="w-full"/>
					</label>
					{(country || persona || category || minScore > 0) && (
						<button onClick={() => { setCountry(''); setPersona(''); setCategory(''); setMinScore(0); }} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-slate-600 hover:bg-slate-100">
							Clear
						</button>
					)}
					<span className="text-slate-500 ml-auto">{visible.length} of {items.length} shown</span>
				</div>

				{error && (
					<div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6 flex items-start gap-3">
						<Warning size={20} className="text-amber-600 mt-0.5"/>
						<div>
							<h3 className="text-sm font-semibold text-amber-900">Couldn't load</h3>
							<p className="text-xs text-amber-800 mt-1">{String(error)}</p>
						</div>
					</div>
				)}

				{loading && <div className="text-center text-sm text-slate-500 py-20">Loading…</div>}

				{!loading && visible.length === 0 && (
					<div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
						No engagement signal in this slice.
					</div>
				)}

				{!loading && visible.length > 0 && (
					<div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
						<table className="w-full text-sm">
							<thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
								<tr>
									<SortHeader {...sort} column="status" className="text-left px-4 py-3">Status</SortHeader>
									<SortHeader {...sort} column="recipient" className="text-left px-4 py-3">Recipient</SortHeader>
									<SortHeader {...sort} column="persona" className="text-left px-4 py-3">Persona</SortHeader>
									<SortHeader {...sort} column="reason" className="text-left px-4 py-3">Why we chose them</SortHeader>
									<SortHeader {...sort} column="campaign_name" className="text-left px-4 py-3">Campaign</SortHeader>
									<SortHeader {...sort} column="country" className="text-left px-4 py-3">Country</SortHeader>
									<SortHeader {...sort} column="opens" numeric className="text-right px-3 py-3" title="Opens"><EnvelopeOpen size={12}/></SortHeader>
									<SortHeader {...sort} column="clicks" numeric className="text-right px-3 py-3" title="Clicks"><CursorClick size={12}/></SortHeader>
									<SortHeader {...sort} column="replies" numeric className="text-right px-3 py-3" title="Replied"><ChatCircle size={12}/></SortHeader>
									<SortHeader {...sort} column="session_seconds" numeric className="text-right px-3 py-3" title="Site visit duration via PostHog"><ChartLine size={12}/></SortHeader>
									<SortHeader {...sort} column="score" numeric className="text-right px-3 py-3">Score</SortHeader>
									<th className="text-left px-3 py-3">Socials</th>
									<SortHeader {...sort} column="last_event_ms" numeric className="text-left px-4 py-3">Last event</SortHeader>
								</tr>
							</thead>
							<tbody>
								{sort.rows.map((r, i) => {
									const badge = STATUS_BADGE[r.status] || STATUS_BADGE.cool;
									const personaCls = PERSONA_BADGE_CLS[r.persona] || 'bg-slate-50 text-slate-700 border-slate-200';
									const replyHref = `mailto:${r.recipient}?subject=Re: ${encodeURIComponent(r.campaign_subject || 'Whipscribe')}`;
									return (
										<tr key={`${r.recipient}-${r.campaign_id}-${i}`} className="border-t border-slate-100 hover:bg-slate-50/40 align-top">
											<td className="px-4 py-3">
												<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>{badge.label}</span>
											</td>
											<td className="px-4 py-3 font-medium text-slate-900">
												{r.recipient}
												{r.name && <div className="text-xs text-slate-500 font-normal mt-0.5">{r.name}</div>}
											</td>
											<td className="px-4 py-3">
												{r.persona ? (
													<>
														<span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${personaCls}`}>{r.persona}</span>
														{r.category && <div className="text-[10px] text-slate-500 mt-0.5">{r.category}{r.variant ? ` · ${r.variant}` : ''}</div>}
													</>
												) : (
													<span className="text-xs text-slate-400">—</span>
												)}
											</td>
											<td className="px-4 py-3 text-slate-600 text-xs">
												<div className="max-w-[260px] truncate" title={r.reason || ''}>{r.reason || '—'}</div>
											</td>
											<td className="px-4 py-3 text-slate-600 text-xs">
												<div className="truncate max-w-[220px]" title={r.campaign_subject || ''}>{r.campaign_subject || r.campaign_name || '—'}</div>
												<div className="flex items-center gap-2 mt-0.5">
													<button onClick={() => navigate(`/dashboard/campaigns/${encodeURIComponent(r.campaign_id)}`)} className="text-xs text-emerald-700 hover:text-emerald-900">↗ campaign</button>
													{r.template_id && (
														<button onClick={() => navigate(`/editor/${encodeURIComponent(r.template_id)}`)} className="inline-flex items-center gap-1 text-xs text-violet-700 hover:text-violet-900">
															<FileText size={10}/> template
														</button>
													)}
												</div>
											</td>
											<td className="px-4 py-3 text-slate-700 text-xs">
												{r.country || '—'}
												{r.domain && <div className="text-[10px] text-slate-400">{r.domain}</div>}
											</td>
											<td className="px-3 py-3 text-right text-slate-700">{r.open}</td>
											<td className="px-3 py-3 text-right text-slate-700">{r.click}</td>
											<td className="px-3 py-3 text-right">{r.replied ? '✓' : '—'}</td>
											<td className="px-3 py-3 text-right text-slate-700">
												{r.site_visit_s > 0 ? (
													<div className="flex items-center justify-end gap-1.5">
														<span className="font-medium text-violet-700" title={`${r.site_pageviews} pageview${r.site_pageviews === 1 ? '' : 's'} · matched via SES click ts + utm`}>
															{r.site_visit_s.toFixed(0)}s
														</span>
														{r.replay_url && (
															<a
																href={r.replay_url}
																target="_blank"
																rel="noreferrer"
																title="Open PostHog session replay"
																className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-violet-100 hover:bg-violet-200 text-violet-700"
															>
																<Play size={10} weight="fill"/>
															</a>
														)}
													</div>
												) : '—'}
											</td>
											<td className="px-3 py-3 text-right text-slate-700 font-medium">{r.score}</td>
											<td className="px-3 py-3">
												<SocialIcons email={r.recipient} name={r.name}/>
											</td>
											<td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
												{fmtAgo(r.last_event_ms)}
												<a href={replyHref} className="block text-emerald-700 hover:text-emerald-900 mt-0.5">↗ reply</a>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}
